import { FC, useContext, useEffect, useState } from 'react'

import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Request } from 'commons/utils/request'
import {
  CircularProgress,
  FF,
  SearchInput,
  SidePanelContent,
  SidePanelContext,
  SidePanelFooter,
  SidePanelHeader,
  Toast,
  Tooltip
} from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { CRBLicensesList } from '~bank-crb-monitor/types'

import { useCrbMonitorListContext } from '../../context/CrbMonitorListContext'
import { CRBPanelContext } from './CRBPanelContext'
import { CRBPanelContent } from './components'

import { s } from './styles'

export const CRBPanel: FC<{ licenseId: number }> = (props) => {
  const { licenseId } = props

  const { refetchList } = useCrbMonitorListContext()

  const [query, setQuery] = useState<string | undefined>(undefined)
  const [selectedLicense, setSelectedLicense] = useState(0)

  const formApi = useForm({
    defaultValues: { query: query || '' } as { query: string }
  })

  const context = useContext(SidePanelContext)

  const { data, isFetching, isFetched, isSuccess } = useQuery<CRBLicensesList>({
    queryKey: ['crb-side-panel', licenseId, query],
    queryFn: () =>
      Request.post(Request.urls.bank.crbMonitorSearchLicenses, {
        licenseId,
        query
      }),
    enabled: licenseId > 0
  })

  useEffect(() => {
    if (isSuccess) {
      setSelectedLicense(data.licenses?.find((el) => el.isSelected)?.id || 0)
      if (data.form.query) {
        formApi.setFieldValue('query', data.form.query)
      }
    }
  }, [isSuccess, data])

  const { mutateAsync } = useMutation<void, Error, { crbId: number; licenseId: number }>({
    mutationFn: (opts) => Request.post(Request.urls.bank.crbMonitorConnect, opts),
    onSuccess: async () => {
      Toast.successSmall(`The License ${licenseId} is already connected`)
      setQuery(undefined)
      refetchList()
      context.block(false)
    },
    onError: () => {
      Toast.errorSmall(
        `The License ${licenseId} could not be connected. Please check your connection parameters or contact 
        technical support.`
      )
      context.block(false)
    }
  })

  const onConnect = () => {
    context.block(true)
    if (selectedLicense) mutateAsync({ crbId: selectedLicense, licenseId })
    else {
      ;(Toast.errorSmall('Please select a license'), context.block(false))
    }
  }

  return (
    <>
      <SidePanelHeader>Connect CRB License</SidePanelHeader>
      <SidePanelContent>
        <s.SearchContainer>
          <formApi.Field
            name="query"
            children={(field) => (
              <FF field={field} label="Search for a license in CRB System" input={(p) => <SearchInput {...p} />} />
            )}
          />
          <Button face="primary" onClick={() => setQuery(formApi.getFieldValue('query'))}>
            Search
          </Button>
        </s.SearchContainer>

        {isFetching && <CircularProgress centered />}
        {!isFetching && isFetched && data && (
          <CRBPanelContext.Provider value={{ data, selectedLicense, setSelectedLicense }}>
            <CRBPanelContent />
          </CRBPanelContext.Provider>
        )}
      </SidePanelContent>
      <SidePanelFooter>
        <s.FooterButtons>
          <Button onClick={() => context.close()}>Cancel</Button>

          {selectedLicense ? (
            <Button loading={context.isBlocked} onClick={onConnect} face="primary">
              Connect
            </Button>
          ) : (
            <Tooltip id="connect" type="dark" event="hover" content="No license selected">
              <Button loading={context.isBlocked} onClick={onConnect} face="primary">
                Connect
              </Button>
            </Tooltip>
          )}
        </s.FooterButtons>
      </SidePanelFooter>
    </>
  )
}
